import { useAuth } from '@/contexts/auth-context';
import { billingContextQueryKey } from '@/hooks/queries/use-billing-context-query';
import { billingSettingsQueryKey } from '@/hooks/queries/use-billing-settings-query';
import { useBillingSettingsScope } from './use-billing-settings-scope';
import { planFeaturesQueryKey } from '@/hooks/queries/use-plan-features-query';
import { SUBSCRIPTION_QUERY_KEY } from '@/hooks/queries/use-subscription-query';
import api from '@/libs/axiosInstance';
import type {
  ApiResponse,
  SwitchToFreePlanRequest,
  SwitchToFreePlanResult,
} from '@fluenceur/types';
import { useMutation, useQueryClient } from '@tanstack/react-query';

/**
 * The brand leaves its paid plan for the free plan: the programme it keeps
 * stays active, the others are paused when its paid period ends. Distinct
 * from cancelling, which refuses while commissions are pending; this touches
 * no commission. Every read that depends on the plan is refreshed after it.
 */
export function useSwitchToFreePlanAction() {
  const queryClient = useQueryClient();
  const { refreshCurrentUser } = useAuth();
  const { scope, assertCurrent } = useBillingSettingsScope();

  return useMutation<SwitchToFreePlanResult, unknown, SwitchToFreePlanRequest, typeof scope>({
    onMutate: () => {
      assertCurrent(scope);
      return scope;
    },
    mutationFn: async ({ keepProgramId }) => {
      assertCurrent(scope);
      const response = await api.post<ApiResponse<SwitchToFreePlanResult>>(
        '/dashboard/subscription/switch-to-free',
        { keepProgramId }
      );
      assertCurrent(scope);
      if (!response.data.success || !response.data.data) {
        throw new Error(response.data.message || 'Failed to switch to the free plan');
      }
      return response.data.data;
    },
    onSuccess: async (_data, _request, target) => {
      if (!target) return;
      try {
        assertCurrent(target);
      } catch {
        return;
      }
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: SUBSCRIPTION_QUERY_KEY }),
        queryClient.invalidateQueries({ queryKey: billingContextQueryKey(target), exact: true }),
        queryClient.invalidateQueries({ queryKey: billingSettingsQueryKey(target), exact: true }),
        queryClient.invalidateQueries({ queryKey: planFeaturesQueryKey }),
        queryClient.invalidateQueries({ queryKey: ['programs'] }),
        refreshCurrentUser(),
      ]);
    },
  });
}
